import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS, SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  first_name: "",
  last_name: "",
  company: "",
  phone: "",
  email: "",
  travel_date: "",
  destination: "",
  service_needed: "",
  message: "",
  website: "",
};

export const LeadForm = ({ defaultService = "", source = "website", dark = false, testId = "lead-form" }) => {
  const [form, setForm] = useState({ ...empty, service_needed: defaultService });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source_page: source });
      toast.success("Thank you — your request has been received.", {
        description: `Our team will follow up with you shortly. Need help now? Call ${SITE.phoneDisplay}.`,
      });
      setForm({ ...empty, service_needed: defaultService });
    } catch (err) {
      toast.error("Something went wrong submitting the form.", {
        description: `Please call us at ${SITE.phoneDisplay} and we'll help you right away.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = dark ? "text-slate-300" : "text-slate-600";
  const inputCls = dark
    ? "bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-gold"
    : "bg-white border-slate-200 focus-visible:ring-gold";

  return (
    <form onSubmit={onSubmit} data-testid={testId} className="space-y-4">
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-first`} className={labelCls}>First Name *</Label>
          <Input id={`${testId}-first`} data-testid={`${testId}-first-name`} required value={form.first_name} onChange={set("first_name")} className={inputCls} autoComplete="given-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-last`} className={labelCls}>Last Name *</Label>
          <Input id={`${testId}-last`} data-testid={`${testId}-last-name`} required value={form.last_name} onChange={set("last_name")} className={inputCls} autoComplete="family-name" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-phone`} className={labelCls}>Phone *</Label>
          <Input id={`${testId}-phone`} data-testid={`${testId}-phone`} required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} autoComplete="tel" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-email`} className={labelCls}>Email *</Label>
          <Input id={`${testId}-email`} data-testid={`${testId}-email`} required type="email" value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-company`} className={labelCls}>Company / Business <span className="font-normal opacity-60">(optional)</span></Label>
          <Input id={`${testId}-company`} data-testid={`${testId}-company`} value={form.company} onChange={set("company")} className={inputCls} autoComplete="organization" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-date`} className={labelCls}>Travel Date</Label>
          <Input id={`${testId}-date`} data-testid={`${testId}-travel-date`} type="date" value={form.travel_date} onChange={set("travel_date")} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-dest`} className={labelCls}>Destination Country</Label>
          <Input id={`${testId}-dest`} data-testid={`${testId}-destination`} value={form.destination} onChange={set("destination")} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Service Needed *</Label>
          <Select value={form.service_needed} onValueChange={(v) => setForm((f) => ({ ...f, service_needed: v }))} required>
            <SelectTrigger data-testid={`${testId}-service-select`} className={inputCls}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} data-testid={`${testId}-service-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testId}-message`} className={labelCls}>Message</Label>
        <Textarea id={`${testId}-message`} data-testid={`${testId}-message`} rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us about your travel plans and what you need help with." />
      </div>
      <button
        type="submit"
        data-testid={`${testId}-submit-button`}
        disabled={submitting}
        className="w-full rounded-full bg-gold py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-gold-dark disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Get Started"}
      </button>
      <p className="text-center text-xs leading-relaxed text-slate-500" data-testid={`${testId}-privacy-note`}>
        Your information is used only to respond to your inquiry. We do not sell or share your personal information.
      </p>
    </form>
  );
};

export default LeadForm;
